/**
 * @suite/tour — the spotlight overlay and step cards.
 *
 * Rendered by the provider into <body>. A step with a resolvable target gets
 * a cut-out spotlight (the real element stays clickable — the tour never
 * locks the UI) and a card anchored to it; a missing target, an empty target
 * or a narrow viewport degrades to a centered card. Esc skips, ←/→ walk,
 * Tab stays inside the card.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { useTour } from "./TourContext";
import type { TourStep } from "./types";
import "./tour.css";

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const VIEW_MARGIN = 16;
const CARD_GAP = 12;
const CARD_WIDTH = 360;
/** Height estimate used for the first placement pass, before the card is measured. */
const CARD_HEIGHT_GUESS = 170;
const SPOTLIGHT_PAD = 6;

function sameRect(a: Rect | null, b: Rect): boolean {
  return (
    a !== null &&
    a.top === b.top &&
    a.left === b.left &&
    a.width === b.width &&
    a.height === b.height
  );
}

/**
 * Live viewport rect of the step target. Re-measures on scroll, resize,
 * element resize and (as a drift safety) on a short poll; returns null when
 * the selector matches nothing, which the card renders as a centered step.
 */
function useTargetRect(selector: string, enabled: boolean): Rect | null {
  const [rect, setRect] = useState<Rect | null>(null);

  useEffect(() => {
    if (!enabled || !selector) {
      setRect(null);
      return undefined;
    }
    let raf = 0;
    let observed: Element | null = null;
    const observer = new ResizeObserver(() => schedule());

    const measure = () => {
      const el = document.querySelector(selector);
      if (!el) {
        observed = null;
        setRect(null);
        return;
      }
      if (observed !== el) {
        observer.disconnect();
        observer.observe(el);
        observed = el;
      }
      const box = el.getBoundingClientRect();
      if (box.width === 0 && box.height === 0) {
        setRect(null);
        return;
      }
      const next: Rect = { top: box.top, left: box.left, width: box.width, height: box.height };
      setRect((prev) => (sameRect(prev, next) ? prev : next));
    };

    const schedule = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(measure);
    };

    measure();
    window.addEventListener("resize", schedule, true);
    window.addEventListener("scroll", schedule, true);
    const poll = window.setInterval(schedule, 500);
    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      observer.disconnect();
      window.removeEventListener("resize", schedule, true);
      window.removeEventListener("scroll", schedule, true);
      window.clearInterval(poll);
    };
  }, [selector, enabled]);

  // Bring a newly targeted element into view once per step.
  useEffect(() => {
    if (!enabled || !selector) return;
    const el = document.querySelector(selector);
    el?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [selector, enabled]);

  return rect;
}

/** Fixed coordinates for the card next to the rect, flipped and clamped to fit. */
function computeCardStyle(
  rect: Rect,
  cardHeight: number,
  placement: TourStep["placement"]
): CSSProperties {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const width = Math.min(CARD_WIDTH, vw - VIEW_MARGIN * 2);
  const height = cardHeight > 0 ? cardHeight : CARD_HEIGHT_GUESS;

  let side: "top" | "bottom" | "left" | "right";
  if (placement === "top" || placement === "bottom" || placement === "left" || placement === "right") {
    side = placement;
  } else {
    const spaceBottom = vh - VIEW_MARGIN - (rect.top + rect.height);
    const spaceTop = rect.top - VIEW_MARGIN;
    const spaceRight = vw - VIEW_MARGIN - (rect.left + rect.width);
    const spaceLeft = rect.left - VIEW_MARGIN;
    side =
      spaceBottom >= height + CARD_GAP
        ? "bottom"
        : spaceTop >= height + CARD_GAP
          ? "top"
          : spaceRight >= width + CARD_GAP
            ? "right"
            : spaceLeft >= width + CARD_GAP
              ? "left"
              : spaceBottom >= spaceTop
                ? "bottom"
                : "top";
  }

  let left = 0;
  let top = 0;
  if (side === "bottom") {
    top = rect.top + rect.height + CARD_GAP;
    left = rect.left + rect.width / 2 - width / 2;
  } else if (side === "top") {
    top = rect.top - CARD_GAP - height;
    left = rect.left + rect.width / 2 - width / 2;
  } else if (side === "left") {
    left = rect.left - CARD_GAP - width;
    top = rect.top + rect.height / 2 - height / 2;
  } else {
    left = rect.left + rect.width + CARD_GAP;
    top = rect.top + rect.height / 2 - height / 2;
  }

  left = Math.min(Math.max(VIEW_MARGIN, left), Math.max(VIEW_MARGIN, vw - width - VIEW_MARGIN));
  top = Math.min(Math.max(VIEW_MARGIN, top), Math.max(VIEW_MARGIN, vh - height - VIEW_MARGIN));

  return { position: "fixed", left: Math.round(left), top: Math.round(top), width };
}

function Spotlight({ rect }: { rect: Rect }) {
  const style: CSSProperties = {
    position: "fixed",
    left: Math.round(rect.left - SPOTLIGHT_PAD),
    top: Math.round(rect.top - SPOTLIGHT_PAD),
    width: Math.round(rect.width + SPOTLIGHT_PAD * 2),
    height: Math.round(rect.height + SPOTLIGHT_PAD * 2),
  };
  return <div className="suite-tour-spotlight" style={style} aria-hidden="true" />;
}

export function TourOverlay() {
  const { phase, config, labels, narrow, stepIndex, next, back, skip, renderStepExtra } = useTour();
  const active = phase === "active" && config !== null;
  const steps = config?.steps ?? [];
  const step: TourStep | null = active && steps[stepIndex] ? steps[stepIndex] : null;

  // The spotlight only renders for anchored steps; narrow viewports and
  // center/missing targets fall through to the centered card.
  const anchored = active && !narrow && !!step && step.target !== "";
  const rect = useTargetRect(anchored && step ? step.target : "", anchored);

  const cardRef = useRef<HTMLDivElement | null>(null);
  const [cardHeight, setCardHeight] = useState(0);

  useLayoutEffect(() => {
    const el = cardRef.current;
    if (!el || !active) return undefined;
    const measure = () => setCardHeight(el.getBoundingClientRect().height);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [active, stepIndex]);

  // Focus the card on open and on every step change; restore on close.
  const previousFocusRef = useRef<Element | null>(null);
  useEffect(() => {
    if (active) {
      if (previousFocusRef.current === null) previousFocusRef.current = document.activeElement;
      const timer = window.setTimeout(() => cardRef.current?.focus(), 0);
      return () => window.clearTimeout(timer);
    }
    const previous = previousFocusRef.current as HTMLElement | null;
    previousFocusRef.current = null;
    previous?.focus?.();
    return undefined;
  }, [active]);

  useEffect(() => {
    if (!active) return;
    const timer = window.setTimeout(() => cardRef.current?.focus(), 0);
    return () => window.clearTimeout(timer);
  }, [active, stepIndex]);

  useEffect(() => {
    if (!active) return undefined;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return;
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        skip();
        return;
      }
      if (e.key === "ArrowRight") {
        e.preventDefault();
        next();
        return;
      }
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        back();
        return;
      }
      if (e.key === "Enter" && !(document.activeElement instanceof HTMLButtonElement)) {
        e.preventDefault();
        next();
        return;
      }
      if (e.key === "Tab" && cardRef.current) {
        const focusables = cardRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (!first || !last) {
          e.preventDefault();
          cardRef.current.focus();
          return;
        }
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [active, next, back, skip]);

  const cardStyle = useMemo<CSSProperties | undefined>(() => {
    if (!rect) return undefined;
    return computeCardStyle(rect, cardHeight, step?.placement);
  }, [rect, cardHeight, step]);

  if (!active || !config || !step) return null;

  const total = steps.length;
  const current = stepIndex + 1;
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === total - 1;
  const stepOf = labels.stepOf ?? ((c: number, t: number) => `${c} of ${t}`);
  const primaryLabel = isFirst && step.target === "" ? labels.start ?? "Start the tour" : isLast ? labels.done ?? "Done" : labels.next ?? "Next";
  const backLabel = labels.back ?? "Back";
  const skipLabel = labels.skip ?? "Skip tour";

  return createPortal(
    <div className="suite-tour-root" data-tour-overlay="" role="dialog" aria-modal="false" aria-label={step.title}>
      {rect && anchored && <Spotlight rect={rect} />}
      <div className={rect && anchored ? "suite-tour-anchored" : "suite-tour-centerwrap"}>
        <div ref={cardRef} className="suite-tour-card" style={cardStyle} tabIndex={-1}>
          <div className="suite-tour-top">
            <span className="suite-tour-count">{stepOf(current, total)}</span>
            <button type="button" className="suite-tour-skip" onClick={skip}>
              {skipLabel}
            </button>
          </div>
          <h2 className="suite-tour-title">{step.title}</h2>
          <p className="suite-tour-body">{step.body}</p>
          {/* Host-provided extra content (the hub's consent row on the last
              step): rendered after the body, inside the card's width. */}
          {renderStepExtra?.(step)}
          <div className="suite-tour-actions">
            {!isFirst && (
              <button type="button" className="suite-tour-btn" onClick={back}>
                {backLabel}
              </button>
            )}
            <div className="suite-tour-dots" aria-hidden="true">
              {steps.map((s, i) => (
                <span key={s.id} className={`suite-tour-dot${i === stepIndex ? " active" : ""}`} />
              ))}
            </div>
            <button type="button" className="suite-tour-btn suite-tour-btn-primary" onClick={next}>
              {primaryLabel}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
